// MUI-free copy of Ui-components/CommonSections/ImageAccordion.
import {memo, useEffect, useId, useState} from 'react';
import type {CSSProperties} from 'react';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import Collapse from '@/components/atoms/Collapse';
import {useHoverAccordion} from '@/hooks/useHoverAccordion';
import styles from './ImageAccordion.module.scss';

export interface ImageAccordionItem {
    title: string;
    /** May carry inline links. */
    description: string;
    image: string | StaticImageData;
    /** Defaults to the title. */
    imageAlt?: string;
}

export interface ImageAccordionProps {
    heading: string;
    /** May carry inline links. */
    subheading: string;
    items: ImageAccordionItem[];
    /** Set when the section sits on a dark background; flips the heading block to white. */
    dark?: boolean;
    /**
     * `stacked` (default): navy list, the image inside each open row. `split`: one white card,
     * the open row's image on the left and numbered rows on the right.
     */
    variant?: 'stacked' | 'split';
    /** `split` only: let the card span the full container instead of the standard wide cap. */
    wide?: boolean;
}

// The split card's fixed height fits this many rows with the longest description open.
const SPLIT_FIXED_ROWS = 6;

const imageStyle = {objectFit: 'cover'} as const;

const IMAGE_SIZES = '(max-width: 900px) 100vw, 40vw';

// The artwork ships with a ~1% transparent shadow border (10px of 1046 at the sides); the slight
// scale pushes it outside the clipped frame so the photo meets the card's edges.
const splitImageStyle = {objectFit: 'cover', objectPosition: 'center top', transform: 'scale(1.04)'} as const;

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');

const pad = (n: number) => String(n).padStart(2, '0');

// Material Icons KeyboardArrowDown (exact path data), drawn with the MUI SvgIcon box (see .chevron in the module).
const Chevron = ({className}: {className: string}) => (
    <svg className={className} viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
    </svg>
);

const ImageAccordion = ({heading, subheading, items, dark = false, variant = 'stacked', wide = false}: ImageAccordionProps) => {
    // The first row starts open so the section never renders as a bare stack of titles.
    // `split` rows also open on mouse hover (see useHoverAccordion); `stacked` stays click-only.
    const {open, setOpen, getTriggerProps} = useHoverAccordion(0);
    // `split`: the image keeps showing the last opened row when every row is closed.
    const [shown, setShown] = useState(0);
    const idPrefix = useId();

    useEffect(() => {
        if (open !== null) setShown(open);
    }, [open]);

    if (!items.length) return null;

    const splitBody = variant === 'split' && (
        // More rows than the fixed height was sized for (six): the card grows with the row count instead.
        <div
            className={cx(styles.splitCard, items.length > SPLIT_FIXED_ROWS && styles.splitCardTall, wide && styles.splitCardWide)}
            style={items.length > SPLIT_FIXED_ROWS ? ({'--split-rows': items.length} as CSSProperties) : undefined}
        >
            {/* Every row's image is server-rendered (with its alt text) and stacked; only the shown one is visible. */}
            <div className={styles.splitImageWrap}>
                {items.map((item, index) => (
                    <div
                        key={item.title}
                        className={cx(styles.splitImageLayer, index === shown && styles.splitImageLayerShown)}
                        aria-hidden={index !== shown}
                    >
                        <OptimizedImage
                            src={item.image}
                            alt={item.imageAlt ?? item.title}
                            fill
                            sizes={IMAGE_SIZES}
                            style={splitImageStyle}
                        />
                    </div>
                ))}
            </div>
            <div className={styles.splitList}>
                {items.map((item, index) => {
                    const isOpen = open === index;
                    const triggerId = `${idPrefix}-trigger-${index}`;
                    const panelId = `${idPrefix}-panel-${index}`;
                    return (
                        <div key={item.title} className={styles.splitItem}>
                            <h3 className={styles.titleWrap}>
                                <button
                                    type="button"
                                    id={triggerId}
                                    aria-expanded={isOpen}
                                    aria-controls={panelId}
                                    {...getTriggerProps(index)}
                                    className={`font-headers ${styles.splitTrigger}`}
                                >
                                    <span className={cx(styles.splitNumber, isOpen && styles.splitNumberOpen)}>{pad(index + 1)}</span>
                                    <span className={styles.splitTitle}>{item.title}</span>
                                    <Chevron className={cx(styles.chevron, styles.splitChevron, isOpen && styles.chevronRotated)} />
                                </button>
                            </h3>
                            {/* Collapse keeps closed panels mounted, so every answer is in the server HTML. */}
                            <Collapse in={isOpen}>
                                <div id={panelId} role="region" aria-labelledby={triggerId} className={styles.splitBody}>
                                    <p className={`font-regular-text ${styles.splitDesc}`}>{parse(item.description)}</p>
                                </div>
                            </Collapse>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div>
            <Container>
                <div className={styles.headerWrap}>
                    <h2 className={cx('font-headers', styles.heading, dark && styles.headingDark)}>{heading}</h2>
                    <p className={cx('font-regular-text', styles.subheading, dark && styles.subheadingDark)}>{parse(subheading)}</p>
                </div>

                {splitBody || (
                    <div className={styles.list}>
                        {items.map((item, index) => {
                            const isOpen = open === index;
                            const triggerId = `${idPrefix}-trigger-${index}`;
                            const panelId = `${idPrefix}-panel-${index}`;
                            return (
                                <div key={item.title} className={cx(styles.item, isOpen ? styles.itemOpen : styles.itemClosed)}>
                                    <h3 className={styles.titleWrap}>
                                        <button
                                            type="button"
                                            id={triggerId}
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                            onClick={() => setOpen(isOpen ? null : index)}
                                            className={cx('font-headers', styles.trigger, isOpen && styles.triggerOpen)}
                                        >
                                            {item.title}
                                            <Chevron
                                                className={cx(styles.chevron, isOpen ? styles.stackedChevronOpen : styles.stackedChevron)}
                                            />
                                        </button>
                                    </h3>

                                    {/* Collapse keeps closed panels mounted, so every answer is in the server HTML. */}
                                    <Collapse in={isOpen}>
                                        <div id={panelId} role="region" aria-labelledby={triggerId} className={styles.body}>
                                            <div className={styles.grid}>
                                                <div className={styles.gridItem}>
                                                    <p className={`font-regular-text ${styles.desc}`}>{parse(item.description)}</p>
                                                </div>
                                                <div className={styles.gridItem}>
                                                    <div className={styles.imageWrap}>
                                                        <OptimizedImage
                                                            src={item.image}
                                                            alt={item.imageAlt ?? item.title}
                                                            fill
                                                            sizes={IMAGE_SIZES}
                                                            style={imageStyle}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Container>
        </div>
    );
};

export default memo(ImageAccordion);
